import type {
  CreatePoolResponse,
  PoolLeaderboardResponse,
  PoolsResponse,
  SkillPool,
} from "@fortress/shared";
import { httpBase } from "../net/origin";
import { UnauthorizedError } from "./api";
import { withAuthRetry } from "./session";
import { getAppState } from "../appState";

// Skill rating pools. The server proxies these to Inhouse — the rating API key
// is never in the browser.

async function authedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const { authDisabled } = await getAppState();
  const base = { "Content-Type": "application/json" };
  if (authDisabled) {
    return fetch(`${httpBase()}${path}`, { ...init, headers: base });
  }
  return withAuthRetry(async (accessToken) => {
    const res = await fetch(`${httpBase()}${path}`, {
      ...init,
      headers: { ...base, Authorization: `Bearer ${accessToken}` },
    });
    if (res.status === 401) throw new UnauthorizedError();
    return res;
  });
}

async function parseError(res: Response): Promise<string> {
  const detail = await res.json().catch(() => ({}));
  return (detail as { error?: string }).error ?? `HTTP ${res.status}`;
}

/** Every pool, plus whether this deployment has ranked play configured. */
export async function listPools(): Promise<PoolsResponse> {
  const res = await authedFetch("/pools");
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<PoolsResponse>;
}

/** One page of a pool's leaderboard, rating-sorted best first. */
export async function fetchPoolLeaderboard(
  poolId: string,
  page: number,
): Promise<PoolLeaderboardResponse> {
  const res = await authedFetch(
    `/pools/${encodeURIComponent(poolId)}/leaderboard?page=${page}`,
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<PoolLeaderboardResponse>;
}

/** Create a pool by name. */
export async function createPool(name: string): Promise<SkillPool> {
  const res = await authedFetch("/pools", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const body = (await res.json()) as CreatePoolResponse;
  return body.pool;
}
